import React, { useEffect, useState } from "react";
import { Card, CardBody } from "reactstrap";
import "./style.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AxiosInstance from "../../AxiosInstance";
import invoice from "../../../assets/White-sidebar-icon/Invoice.svg";
import { CardHeader, CardTitle, Table } from "reactstrap";
import {
  Box,
  Checkbox,
  Collapse,
  Grid,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { LoaderComponent } from "../../../components/Icon/Index";
import WhiteButton from "../../../components/Button/WhiteButton";
import BlueButton from "../../../components/Button/BlueButton";
import { Typography } from "@mui/material";
import { handleAuth } from "../../../components/Login/Auth";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useUserType } from "../../../contexts/UserTypeContext";
import CustomTooltip from "../../../components/Tooltip/CustomTooltip";
import { useMediaQuery } from "@mui/material";
import { useUserTimezoneFormatter } from "../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import {
  JobberStatusBadge,
  getJobberStatusTone,
} from "../../../components/MuiTable/index.jsx";

const InvoiceTable = () => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [customerData, setCustomerData] = useState([]);
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);
  const [selectedVisits, setSelectedVisits] = useState([]);
  const [loader, setLoader] = useState(false);
  const [collapseIndex, setCollapseIndex] = useState(null);
  const [visitCollapseIndex, setVisitCollapseIndex] = useState(null);
  const navigate = useNavigate();
  const { CompanyUrl } = useParams();
  const baseUrl = import.meta.env.VITE_BASE_API;
  const location = useLocation();
  const [tokenDecode, setTokenDecode] = useState();
  const CustomerId = location?.state?.CustomerId;
  const LocationId = location?.state?.LocationId;
  const ContractId = location?.state?.ContractId;

  // Use timezone-aware date formatter hook
  const formatDateWithUserTimezone = useUserTimezoneFormatter();

  // Helper function to format address
  const formatAddress = (customerData) => {
    if (!customerData) return "Address not available";
    
    const address = customerData?.Address || "";
    const city = customerData?.City || "";
    const state = customerData?.State || "";
    const zip = customerData?.Zip || "";
    
    // Check if all address fields are empty
    if (!address && !city && !state && !zip) {
      return "Address not available";
    }
    
    // Build address string with only available fields
    const addressParts = [];
    if (address) addressParts.push(address);
    if (city) addressParts.push(city);
    if (state) addressParts.push(state);
    if (zip) addressParts.push(zip);
    
    return addressParts.join(", ");
  };

  const formatContractAmount = (total) => {
    if (total == null || total === "") return "N/A";
    const n = Number(total);
    if (Number.isNaN(n)) return "N/A";
    return n.toFixed(2);
  };

  const fetchDatas = async () => {
    try {
      const res = await handleAuth(navigate, location);
      setTokenDecode(res.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchDatas();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!CustomerId) return;
      setLoader(true);
      try {
        // Fetch both contract and quote data
        const [contractRes, quoteRes] = await Promise.all([
          AxiosInstance.get(`/v1/contract/get_invoice_data/${CustomerId}`),
          AxiosInstance.get(`/v1/quote/quote_details/${location?.state?.QuoteId}`)
        ]);

        const contractData = contractRes?.data?.data[0];
        const quoteData = quoteRes?.data?.data;
        // If we have a specific quote ID, add it to the contracts list
        const quoteAsContract = quoteData ? {
          ...quoteData,
          ContractId: `quote_${quoteData.QuoteId}`,
          ContractNumber: quoteData.QuoteNumber,
          isQuote: true
        } : null;

        if (LocationId && contractData?.locations) {
          const specificLocation = contractData?.locations?.find(
            (loc) => loc?.LocationId === LocationId
          );
          if (specificLocation) {
            const filteredData = {
              ...contractData,
              Address: specificLocation?.Address,
              City: specificLocation?.City,
              State: specificLocation?.State,
              Country: specificLocation?.Country,
              Zip: specificLocation?.Zip,
              Contracts: [...(specificLocation?.Contracts || []), ...(quoteAsContract ? [quoteAsContract] : [])],
            };
            setCustomerData(filteredData);
            
            if (ContractId) {
              const selectedContract = filteredData.Contracts.find(
                (contract) => contract.ContractId === ContractId
              );
              if (selectedContract) {
                setSelectedCustomerData(selectedContract);
              }
            } else if (location?.state?.QuoteId && quoteAsContract) {
              setSelectedCustomerData(quoteAsContract);
            }
          } else {
            setCustomerData({
              ...contractData,
              Contracts: quoteAsContract ? [quoteAsContract] : [],
            });
          }
        } else {
          const allContracts =
            contractData?.locations?.reduce((acc, location) => {
              return [...acc, ...(location?.Contracts || [])];
            }, []) || [];

          if (quoteAsContract) {
            allContracts.push(quoteAsContract);
          }

          setCustomerData({
            ...contractData,
            Contracts: allContracts,
          });

          if (ContractId) {
            const selectedContract = allContracts.find(
              (contract) => contract.ContractId === ContractId
            );
            if (selectedContract) {
              setSelectedCustomerData(selectedContract);
            }
          } else if (location?.state?.QuoteId && quoteAsContract) {
            setSelectedCustomerData(quoteAsContract);
          }
        }
      } catch (error) { 
      } finally {
        setLoader(false);
      }
    };
    fetchData();
  }, [CustomerId, LocationId, ContractId, location?.state?.QuoteId, baseUrl]);

  const handleCheckboxChange = (item) => {
    setSelectedCustomerData((prev) =>
      prev?.ContractId === item.ContractId ? null : item
    );
    // Clear visits if this is a quote or if deselecting
    if (item.isQuote || !item) {
      setSelectedVisits([]);
    }
  };

  const handleRowClick = (item, index) => {
    if (isMobile) {
      setCollapseIndex(collapseIndex === index ? null : index);
    } else {
      handleCheckboxChange(item);
    }
  };

  const handleRowExpandClick = (event, index) => {
    event.stopPropagation();
    setCollapseIndex(collapseIndex === index ? null : index);
  };

  const handleCheckboxClick = (event, item) => {
    event.stopPropagation();
    handleCheckboxChange(item);
  };
  const handleClick = () => {
    let navigationState = [];
    let navigationUrl = "";
    if (location?.pathname?.includes("staff-member")) {
      navigationState = [`/index`, `/staff-member`, `/worker-invoice`];
      navigationUrl = `/${CompanyUrl}/staff-member/worker-invoice`;
    } else if (!location?.pathname?.includes("staff-member")) {
      navigationState = [`/index`, `/invoice`];
      navigationUrl = `/${CompanyUrl}/invoice`;
    }

    navigate(navigationUrl, {
      state: {
        navigats: navigationState,
      },
    });
  };

  const minimize = (str) => {
    str = str.slice(0, 20);
    return str;
  };

  const handleVisitCheckboxChange = (event, visit) => {
    event.stopPropagation();
    if (event.target.checked) {
      setSelectedVisits((prev) => [...prev, visit]);
    } else {
      setSelectedVisits((prev) =>
        prev?.filter((v) => v?.VisitId !== visit?.VisitId)
      );
    }
  };

  const handleVisitRowClick = (visit, index) => {
    if (isMobile) {
      setVisitCollapseIndex(visitCollapseIndex === index ? null : index);
    }
  };

  const handleVisitRowExpandClick = (event, index) => {
    event.stopPropagation();
    setVisitCollapseIndex(visitCollapseIndex === index ? null : index);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return formatDateWithUserTimezone(dateString, {
      dateFormat: "MM/DD/YYYY",
      includeSeconds: false
    });
  };

  const handleNextClick = () => {
    const isWorker = tokenDecode?.Role === "Worker";

    let navigationState = isWorker
      ? [
          "/index",
          "/staff-member/worker-invoice",
          "/staff-member/workerinvoicetable",
          "/staff-member/addinvoice",
        ]
      : ["/index", "/invoice", "/invoicetable", "/addinvoice"];
    const targetPath = isWorker
      ? `/${CompanyUrl}/staff-member/addinvoice`
      : `/${CompanyUrl}/addinvoice`;

    
    // Check if we're coming from a quote
    const isFromQuote = location?.state?.QuoteId || selectedCustomerData?.isQuote;
    
    navigate(targetPath, {
      state: {
        navigats: navigationState,
        id: selectedCustomerData?.ContractId,
        QuoteId: isFromQuote ? (location?.state?.QuoteId || selectedCustomerData?.QuoteId) : undefined,
        formData: isFromQuote ? selectedCustomerData : undefined,
        visits:
          selectedCustomerData?.IsPerVisit && selectedVisits.length > 0
            ? selectedVisits
            : selectedCustomerData?.IsFixedPrice
            ? []
            : selectedCustomerData?.visits || [],
        selectedVisits: selectedCustomerData?.IsPerVisit ? selectedVisits : [],
        contractLineItems: selectedCustomerData?.lineItems || [],
        products: selectedCustomerData?.products || [],
        // Add quote-specific data
        CustomerId: location?.state?.CustomerId || CustomerId,
        LocationId: location?.state?.LocationId || LocationId,
      },
      replace: false
    });
  };

  const handleQuickInvoiceClick = async () => {
    const isWorker = tokenDecode?.Role === "Worker";

    let navigationState = isWorker
      ? [
          "/index",
          "/worker-invoice",
          "/workerinvoicetable",
          "/addinvoice",
        ]
      : ["/index", "/invoice", "/invoicetable", "/addinvoice"];
    const targetPath = isWorker
      ? `/${CompanyUrl}/staff-member/addinvoice`
      : `/${CompanyUrl}/addinvoice`;

    // Prepare customer data for quick invoice
   
    
    // Try to fetch customer contact details from API
    let customerEmail = "Email not available";
    let customerPhone = "Phone not available";
    
    try {
      // Try multiple API endpoints to get customer contact details
    
      
      // Try different possible endpoints
      const endpoints = [
        `/v1/customer/get_customer/${CustomerId}`,
        `/v1/customer/${CustomerId}`,
        `/v1/user/get_user/${CustomerId}`,
        `/v1/user/${CustomerId}`
      ];
      
      let customerDetails = null;
      
      for (const endpoint of endpoints) {
        try {
       
          const customerRes = await AxiosInstance.get(endpoint);
         
          
          if (customerRes?.data?.statusCode === 200 && customerRes?.data?.data) {
            customerDetails = customerRes.data.data;
           
            break;
          }
        } catch (endpointError) {
        }
      }
      
      if (customerDetails) {
        customerEmail = customerDetails?.Email || customerDetails?.EmailAddress || customerDetails?.email || customerDetails?.emailAddress || customerDetails?.EmailId || "Email not available";
        customerPhone = customerDetails?.Phone || customerDetails?.PhoneNumber || customerDetails?.phone || customerDetails?.phoneNumber || customerDetails?.Mobile || customerDetails?.ContactNumber || "Phone not available";
      
      } else {
      
        
        
        // Look for any field that might contain email or phone
        const allValues = Object.values(customerData);
        const allKeys = Object.keys(customerData);
        
        // Search for email-like values
        for (let i = 0; i < allValues.length; i++) {
          const value = allValues[i];
          if (typeof value === 'string' && value.includes('@') && value.includes('.')) {
            customerEmail = value;
            
            break;
          }
        }
        
        // Search for phone-like values
        for (let i = 0; i < allValues.length; i++) {
          const value = allValues[i];
          if (typeof value === 'string' && /[\d\-\+\(\)\s]{10,}/.test(value)) {
            customerPhone = value;
           
            break;
          }
        }
        
        if (customerEmail === "Email not available") {
          customerEmail = "Email not available";
        }
        if (customerPhone === "Phone not available") {
          customerPhone = "Phone not available";
        }
      }
    } catch (error) {
    
      customerEmail = "Email not available";
      customerPhone = "Phone not available";
    }
   
    
    // Get customer data from different sources
    const customerInfo = customerData?.customer || customerData?.customerData || {};
    const locationInfo = customerData?.location || customerData?.locationData || {};
    let firstName = customerData?.FirstName || customerData?.firstName || customerInfo?.FirstName || customerInfo?.firstName || "";
    let lastName = customerData?.LastName || customerData?.lastName || customerInfo?.LastName || customerInfo?.lastName || "";
    let customerName = `${firstName} ${lastName}`.trim() || "Customer Name";
   
    const quickInvoiceData = {
      customerName: customerName,
      customerAddress: customerData?.Address || 
                      locationInfo?.Address || 
                      customerInfo?.Address || 
                      "Address not available",
      customerCity: customerData?.City || 
                   locationInfo?.City || 
                   customerInfo?.City || 
                   "City not available",
      customerState: customerData?.State || 
                    locationInfo?.State || 
                    customerInfo?.State || 
                    "State not available",
   
      customerZip: customerData?.Zip || 
                  locationInfo?.Zip || 
                  customerInfo?.Zip || 
                  "Zip not available",
      customerEmail: customerEmail,
      customerPhone: customerPhone,
      isQuickInvoice: true, // Flag to indicate this is a quick invoice
    };
   

    navigate(targetPath, {
      state: {
        navigats: navigationState,
        quickInvoiceData: quickInvoiceData,
        customerId: CustomerId,
        locationId: LocationId,
        // Add quote-specific data if available
        QuoteId: location?.state?.QuoteId,
        formData: selectedCustomerData?.isQuote ? selectedCustomerData : undefined,
        products: selectedCustomerData?.products || [],
        contractLineItems: selectedCustomerData?.lineItems || [],
      },
    });
  };
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";

  return (
    <Grid>
      <BlueButton
        isDonationMode={isDonationMode}
        className="customer-details-back-btn"
        aria-label="Go back"
        label={
          <ArrowBackOutlinedIcon sx={{ fontSize: 22, color: "#ffffff" }} />
        }
        onClick={handleClick}
        style={{
          marginRight: "10px",
          width: "50px",
          height: "40px",
          minHeight: "40px",
          marginBottom: "10px",
          padding: "0px",
          borderRadius: "8px",
          marginTop: "10px",
        }}
      />
      <Card className="my-2" style={{ borderRadius: "15px" }}>
        <CardHeader
          className="invoice-header invoiceChoiceImg"
          style={{ background: "transparent" }}
        >
          <div className="d-flex justify-content-between align-items-center mt-1 invoice-header-container">
            <Typography className="d-flex invoice-header-left">
              <Typography
                className={`mb-2 invoiceAddIcon ${
                  isDonationMode ? "bg-donor-color" : "bg-blue-color"
                }`}
                style={{
                  borderRadius: "50%",
                  width: "49px",
                  marginTop: isMobile ? "10px" : "0px",
                  height: "49px",
                  padding: "10px 2px 12px 15px",
                }} 
              >
                <img src={invoice || "/placeholder.svg"} alt="Invoice Icon" />
              </Typography>
              <Typography
                style={{
                  fontSize: "27px",
                  fontWeight: "700",
                  marginTop: "7px",
                  marginLeft: "10px",
                }}
                className="invoiceFontSize text-blue-color"
              >
                New Invoice
              </Typography>
            </Typography>
            {!isDonationMode && (
            <BlueButton
              onClick={handleQuickInvoiceClick}
              isDonationMode={isDonationMode}
              label="New Invoice"
              className="invoice-header-button"
              style={{
                fontSize: "14px",
                padding: "8px 16px",
                height: "40px",
                borderRadius: "8px",
              }}
            />
            )}
          </div>
        </CardHeader>
        <CardBody>
          <CardTitle tag="h5" className="text-blue-color">
            {isDonationMode
              ? "Select the donor to create an invoice for:"
              : "Select the contract for which you want to create an invoice:"}
          </CardTitle>
        </CardBody>
        <Grid
          className=""
          style={{ paddingLeft: "10px", paddingRight: "10px" }}
        >
          {loader ? (
            <Grid className="d-flex flex-direction-row justify-content-center align-items-center p-5 m-5">
              <LoaderComponent variant="skeleton"
                isDonationMode={isDonationMode}
                loader={loader}
                height="50"
                width="50"
              />
            </Grid>
          ) : customerData?.Contracts?.length >= 1 ? (
            <Grid
              className="contract-grid-container"
              style={{
                border: "1px solid rgba(6, 49, 100, 0.3)",
                borderRadius: "20px",
                maxHeight: "400px",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <Table
                borderless
                className="mb-0 contract-table"
              >
                <TableHead>
                  <TableRow>
                    {isMobile && (
                      <TableCell className="backgroundColor-table contract-table-expand" style={{ width: "30px" }}>
                        <Typography></Typography>
                      </TableCell>
                    )}
                    <TableCell className="backgroundColor-table contract-table-checkbox">
                      <Typography></Typography>
                    </TableCell>
                    <TableCell className="backgroundColor-table contract-table-contract">
                      <Typography className="invoiceTable-heade">
                        Contract
                      </Typography>
                    </TableCell>
                    <TableCell className="backgroundColor-table contract-table-status">
                      <Typography className="invoiceTable-heade">
                        Status
                      </Typography>
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell className="backgroundColor-table contract-table-address">
                          <Typography className="invoiceTable-heade">
                            Address
                          </Typography>
                        </TableCell>
                        <TableCell className="backgroundColor-table contract-table-amount" style={{ textAlign: "right" }}>
                          <Typography className="invoiceTable-heade">
                            Amount
                          </Typography>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerData?.Contracts?.map((item, index) => (
                    <React.Fragment key={item.ContractId}>
                      <TableRow
                        className="invoice-table contract-table-row"
                        onClick={() => handleRowClick(item, index)}
                        style={{
                          borderBottom:
                            index !== customerData?.Contracts?.length - 1
                              ? "1px solid rgba(6, 49, 100, 0.3)"
                              : "none",
                          cursor: "pointer",
                        }}
                      >
                        {isMobile && (
                          <TableCell
                            className="contract-table-expand"
                            style={{
                              width: "30px",
                              textAlign: "center",
                              padding: "4px",
                            }}
                            onClick={(e) => handleRowExpandClick(e, index)}
                          >
                            <IconButton size="small" style={{ padding: "4px" }}>
                              {collapseIndex === index ? (
                                <ArrowDropUpIcon className="text-blue-color" style={{ fontSize: "20px" }} />
                              ) : (
                                <ArrowDropDownIcon className="text-blue-color" style={{ fontSize: "20px" }} />
                              )}
                            </IconButton>
                          </TableCell>
                        )}
                        <TableCell
                        component="th"
                        scope="row"
                        style={{
                          borderTopLeftRadius: index === 0 ? "20px" : "0",
                          borderBottomLeftRadius:
                            index === customerData?.Contracts?.length - 1
                              ? "20px"
                              : "0",
                          padding: "15px",
                        }}
                      >
                        <Checkbox
                          id={`checkbox-${item?.ContractId}`}
                          sx={{ marginLeft: "8px", p: 0.5 }}
                          checked={
                            selectedCustomerData?.ContractId === item?.ContractId
                          }
                          onClick={(event) => handleCheckboxClick(event, item)}
                          color="default"
                        />
                      </TableCell>

                      <TableCell
                        className="text-blue-color contract-table-contract"
                        style={{
                          padding: "15px",
                          fontWeight: "600",
                        }}
                      >
                          {item.isQuote 
                            ? `#${item?.QuoteNumber || "N/A"} ${item?.Title || "N/A"} (Quote)`
                            : `#${item?.ContractNumber || "N/A"} ${item?.Title || "N/A"}`
                          }
                      </TableCell>
                      <TableCell
                        className="contract-table-status"
                        style={{
                          padding: "15px",
                          paddingLeft: "10px",
                          paddingRight: "10px",
                        }}
                      >
                        <JobberStatusBadge
                          tone={getJobberStatusTone(item?.Status)}
                        >
                          {item?.Status || "N/A"}
                        </JobberStatusBadge>
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell
                            className="text-blue-color contract-table-address"
                        style={{
                          padding: "15px",
                        }}
                      >
                        <Box
                          style={{
                            position: "relative",
                            display: "inline-block",
                          }}
                          sx={{
                            "&:hover .address": {
                              display: "inline-block",
                            },
                          }}
                        >
                          <Typography
                            className="contract-address-text"
                            sx={{
                              display: "block",
                              fontWeight: "600",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              lineHeight: "1.5"
                            }}
                          >
                            {formatAddress(customerData)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        className="text-blue-color"
                        style={{
                          borderTopRightRadius: index === 0 ? "20px" : "0",
                          borderBottomRightRadius:
                            index === customerData?.Contracts?.length - 1
                              ? "20px"
                              : "0",
                          padding: "15px",
                          fontWeight: "600",
                          textAlign: "right",
                        }}
                      >
                        ${formatContractAmount(item?.Total)}
                      </TableCell>
                        </>
                      )}
                      </TableRow>
                      {isMobile && collapseIndex === index && (
                        <TableRow>
                          <TableCell
                            className="contract-collapse-cell"
                            style={{ paddingBottom: 0, paddingTop: 0, paddingLeft: 0, paddingRight: 0 }}
                            colSpan={4}
                          >
                            <Collapse in={true} timeout="auto" unmountOnExit>
                              <div className="mobile-table-collapse">
                                <div className="mobile-collapse-content">
                                  <div className="mobile-data-row">
                                    <span className="mobile-data-label">Address:</span>
                                    <div className="mobile-data-value">
                                      <Typography
                                        className="contract-address-text"
                                        sx={{
                                          fontWeight: "600",
                                          whiteSpace: "normal",
                                          wordBreak: "break-word",
                                          lineHeight: "1.5"
                                        }}
                                      >
                                        {formatAddress(customerData)}
                                      </Typography>
                                    </div>
                                  </div>
                                  <div className="mobile-data-row">
                                    <span className="mobile-data-label">Amount:</span>
                                    <div className="mobile-data-value">
                                      ${formatContractAmount(item?.Total)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          ) : (
            <Grid className="d-flex justify-content-center align-items-center my-5 text-blue-color">
              <Typography>
                {isDonationMode
                  ? LocationId
                    ? "Select the donor to create an invoice for this location"
                    : "No Donors Available"
                  : LocationId
                  ? "No Contracts Available For This Location"
                  : "No Contracts Available"}
              </Typography>
            </Grid>
          )}
        </Grid>
        {selectedCustomerData?.IsPerVisit &&
          selectedCustomerData?.IsRecuringJob && (
            <Grid className="p-4">
              <CardTitle tag="h5" className="text-blue-color mb-3">
                Select the visits you want to include in the invoice:
              </CardTitle>
              <Grid
                className="visits-grid-container"
                style={{
                  border: "1px solid rgba(6, 49, 100, 0.3)",
                  borderRadius: "20px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <Table
                  borderless
                  className="mb-0 visits-table"
                >
                  <TableHead>
                    <TableRow>
                      {isMobile && (
                        <TableCell className="backgroundColor-table visits-table-expand" style={{ width: "30px" }}>
                          <Typography></Typography>
                        </TableCell>
                      )}
                      <TableCell className="backgroundColor-table visits-table-checkbox">
                        <Typography></Typography>
                      </TableCell>
                      <TableCell className="backgroundColor-table visits-table-contract">
                        <Typography className="invoiceTable-heade">
                          Contract Number
                        </Typography>
                      </TableCell>
                      <TableCell className="backgroundColor-table visits-table-status">
                        <Typography className="invoiceTable-heade">
                          Status
                        </Typography>
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell className="backgroundColor-table visits-table-start-date">
                            <Typography className="invoiceTable-heade">
                              Start date
                            </Typography>
                          </TableCell>
                          <TableCell className="backgroundColor-table visits-table-end-date">
                            <Typography className="invoiceTable-heade">
                              End date
                            </Typography>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCustomerData?.visits
                      ?.filter((visit) => visit.Status === "Completed")
                      .map((visit, index, filteredVisits) => (
                        <React.Fragment key={visit.VisitId || index}>
                          <TableRow
                            className="visits-table-row"
                            onClick={() => handleVisitRowClick(visit, index)}
                            style={{
                              borderBottom:
                                index !== filteredVisits.length - 1
                                  ? "1px solid rgba(6, 49, 100, 0.3)"
                                  : "none",
                              cursor: "pointer",
                            }}
                          >
                            {isMobile && (
                              <TableCell
                                className="visits-table-expand"
                                style={{
                                  width: "30px",
                                  textAlign: "center",
                                  padding: "4px",
                                }}
                                onClick={(e) => handleVisitRowExpandClick(e, index)}
                              >
                                <IconButton size="small" style={{ padding: "4px" }}>
                                  {visitCollapseIndex === index ? (
                                    <ArrowDropUpIcon className="text-blue-color" style={{ fontSize: "20px" }} />
                                  ) : (
                                    <ArrowDropDownIcon className="text-blue-color" style={{ fontSize: "20px" }} />
                                  )}
                                </IconButton>
                              </TableCell>
                            )}
                            <TableCell
                              component="th"
                              scope="row"
                              className="visits-table-checkbox"
                              style={{
                                borderTopLeftRadius: index === 0 && !isMobile ? "20px" : "0",
                                borderBottomLeftRadius:
                                  index === filteredVisits.length - 1 && !isMobile
                                    ? "20px"
                                    : "0",
                                padding: isMobile ? "4px" : "15px",
                              }}
                            >
                              <Checkbox
                                sx={{
                                  marginLeft: isMobile ? "0px" : "8px",
                                  p: 0.5,
                                }}
                                checked={selectedVisits.some(
                                  (v) => v.VisitId === visit.VisitId
                                )}
                                onChange={(e) =>
                                  handleVisitCheckboxChange(e, visit)
                                }
                                color="default"
                              />
                            </TableCell>
                            <TableCell
                              className="text-blue-color visits-table-contract"
                              style={{
                                padding: isMobile ? "10px 8px" : "15px",
                                fontWeight: "600",
                              }}
                            >
                              #{selectedCustomerData.ContractNumber}
                            </TableCell>
                            <TableCell
                              className="visits-table-status"
                              style={{
                                padding: isMobile ? "10px 8px" : "15px",
                                color: "#2E7D32",
                                paddingLeft: isMobile ? "8px" : "10px",
                                paddingRight: isMobile ? "8px" : "10px",
                              }}
                            >
                              {visit?.Status}
                            </TableCell>
                            {!isMobile && (
                              <>
                                <TableCell
                                  className="text-blue-color visits-table-start-date"
                                  style={{
                                    padding: "15px",
                                  }}
                                >
                                  {formatDate(visit?.StartDate)}
                                </TableCell>
                                <TableCell
                                  className="text-blue-color visits-table-end-date"
                                  style={{
                                    borderTopRightRadius: index === 0 ? "20px" : "0",
                                    borderBottomRightRadius:
                                      index === filteredVisits.length - 1
                                        ? "20px"
                                        : "0",
                                    padding: "15px",
                                  }}
                                >
                                  {formatDate(visit?.EndDate)}
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                          {isMobile && visitCollapseIndex === index && (
                            <TableRow>
                              <TableCell
                                className="visits-collapse-cell"
                                style={{ paddingBottom: 0, paddingTop: 0, paddingLeft: 0, paddingRight: 0 }}
                                colSpan={4}
                              >
                                <Collapse in={true} timeout="auto" unmountOnExit>
                                  <div className="mobile-table-collapse">
                                    <div className="mobile-collapse-content">
                                      <div className="mobile-data-row">
                                        <span className="mobile-data-label">Start Date:</span>
                                        <div className="mobile-data-value">
                                          {formatDate(visit?.StartDate)}
                                        </div>
                                      </div>
                                      <div className="mobile-data-row">
                                        <span className="mobile-data-label">End Date:</span>
                                        <div className="mobile-data-value">
                                          {formatDate(visit?.EndDate)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    {selectedCustomerData?.visits?.every(
                      (visit) => visit?.Status !== "Completed"
                    ) && (
                      <TableRow>
                        <TableCell
                          colSpan={isMobile ? 4 : 5}
                          style={{
                            textAlign: "center",
                            padding: "15px",
                            fontSize: "16px",
                            color: "#1E3A8A",
                          }}
                        >
                          There are no completed visits for this contract.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          )}

        <Grid
          className="BlueAndWhiteBtmFlex invoiceBtnGap"
          style={{
            margin: "10px 30px 20px 30px",
            justifyContent: "space-between",
            display: "flex",
          }}
        >
          <WhiteButton onClick={() => navigate(-1)} label="Cancel" />
          <BlueButton
            onClick={handleNextClick}
            isDonationMode={isDonationMode}
            disabled={
              !selectedCustomerData ||
              (selectedCustomerData?.IsPerVisit &&
                selectedCustomerData?.visits?.filter(
                  (v) => v?.Status === "Completed"
                ).length > 0 &&
                selectedVisits.length === 0)
            }
            label="Next"
          />
        </Grid>
      </Card>
    </Grid>
  );
};

export default InvoiceTable;
